import {
  Animated,
  LayoutChangeEvent,
  TouchableOpacity,
  View,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { shadow } from '@/styles';
import { Typography } from './typography';
import { useEffect, useRef, useState } from 'react';

// we only support 2 tab toggle for now
interface ToggleProps<T extends string> {
  onPress: (value: T) => void;
  selectedTab: T;
  tabs: {
    label: string;
    value: T;
  }[];
}

export const Toggle: <T extends string>(
  props: ToggleProps<T>,
) => React.ReactNode = ({ onPress, selectedTab, tabs }) => {
  const [styles] = useStyles();
  const [width, setWidth] = useState(0);
  const animatedValue = useRef(
    new Animated.Value(selectedTab === tabs[1].value ? 1 : 0),
  ).current;

  useEffect(() => {
    Animated.timing(animatedValue, {
      duration: 300,
      toValue: selectedTab === tabs[1].value ? 1 : 0,
      useNativeDriver: false,
    }).start();
  }, [animatedValue, selectedTab, tabs]);

  const translateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0, width / 2],
  });

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setWidth(width);
  };

  return (
    <View style={styles.container}>
      <View style={styles.background}>
        <View onLayout={handleLayout} style={styles.innerContainer}>
          <Animated.View
            style={[
              styles.button,
              styles.selectedButton,
              {
                transform: [{ translateX }],
              },
            ]}
          />
          <TouchableOpacity
            accessibilityLabel={tabs[0].label}
            onPress={() => onPress(tabs[0].value)}
            style={styles.baseButton}
          >
            <Typography variant="paragraph-md-600">{tabs[0].label}</Typography>
          </TouchableOpacity>
          <TouchableOpacity
            accessibilityLabel={tabs[1].label}
            onPress={() => onPress(tabs[1].value)}
            style={styles.baseButton}
          >
            <Typography variant="paragraph-md-600">{tabs[1].label}</Typography>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  background: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderRadius: 40,
    flexDirection: 'row',
    height: 40,
    padding: 4,
  },
  baseButton: {
    alignItems: 'center',
    borderRadius: 24,
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  button: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: 24,
    ...shadow({
      color: theme.colors.black,
      elevation: 1,
      opacity: 0.05,
      radius: 4,
      x: 0,
      y: 2,
    }),
  },
  container: {
    alignSelf: 'center',
    width: '100%',
  },
  innerContainer: {
    flex: 1,
    flexDirection: 'row',
    height: 32,
  },
  selectedButton: {
    height: 32,
    left: 0,
    position: 'absolute',
    top: 0,
    width: '50%',
  },
}));
